import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Rocket, 
  Mail, 
  CheckCircle2, 
  Briefcase, 
  FileText, 
  TrendingUp,
  Sparkles,
  Bell,
  Gift,
  Users
} from 'lucide-react';
import { UpliftShield, UpliftLogoWhite } from '../components/UpliftLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Set your launch date here (format: Year, Month-1, Day, Hour, Minute)
const LAUNCH_DATE = new Date(2026, 3, 15, 9, 0, 0); // April 15, 2026 at 9:00 AM

const LaunchCountdown = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  // Countdown timer
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = LAUNCH_DATE - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, []);

  // Fetch subscriber count
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await fetch(`${API_URL}/api/subscribers/count`);
        if (res.ok) {
          const data = await res.json();
          setSubscriberCount(data.count);
        }
      } catch (error) {
        // Non-critical: subscriber count fetch failed, use default
        console.debug('Subscriber count fetch failed:', error.message);
      }
    };
    fetchCount();
  }, [isSubscribed]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await fetch(`${API_URL}/api/subscribers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await res.json();

      if (res.ok) {
        setIsSubscribed(true);
        toast.success('You\'re on the list! We\'ll notify you at launch.');
      } else {
        if (data.detail === 'Email already subscribed') {
          toast.info('You\'re already on the list! We\'ll notify you at launch.');
          setIsSubscribed(true);
        } else {
          toast.error(data.detail || 'Something went wrong');
        }
      }
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const products = [
    {
      name: 'CareerBoost',
      description: 'AI resume builder & job tracker',
      icon: <Briefcase className="w-5 h-5" />,
      color: 'bg-indigo-500'
    },
    {
      name: 'BizPlanAI',
      description: 'Professional business plans in minutes',
      icon: <FileText className="w-5 h-5" />,
      color: 'bg-emerald-500'
    },
    {
      name: 'CreditBoost',
      description: 'Personalized credit improvement plans',
      icon: <TrendingUp className="w-5 h-5" />,
      color: 'bg-blue-500'
    }
  ];

  const earlyBirdPerks = [
    { icon: <Gift className="w-5 h-5" />, text: '20% off lifetime access' },
    { icon: <Bell className="w-5 h-5" />, text: 'First to know at launch' },
    { icon: <Sparkles className="w-5 h-5" />, text: 'Exclusive early features' },
    { icon: <Users className="w-5 h-5" />, text: 'Founding member badge' }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <header className="py-6 px-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <UpliftLogoWhite size="sm" showText={true} />
            </Link>
            <Link to="/">
              <Button variant="ghost" className="text-slate-400 hover:text-white">
                Back to Home
              </Button>
            </Link>
          </div>
        </header>

        {/* Hero */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/20 border border-indigo-500/30 text-indigo-300 text-sm font-medium mb-8">
              <Rocket className="w-4 h-4" />
              Launching Soon
            </div>

            {/* Shield */}
            <div className="flex justify-center mb-8">
              <div className="relative">
                <UpliftShield size={100} color="#6366F1" />
                <div className="absolute inset-0 animate-ping">
                  <UpliftShield size={100} color="#6366F1" />
                </div>
              </div>
            </div>

            {/* Headline */}
            <h1 
              className="text-4xl md:text-6xl font-bold mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Something Big is Coming
            </h1>
            <p className="text-xl text-slate-400 mb-12 max-w-2xl mx-auto">
              Three AI-powered tools to transform your career, business, and finances. 
              Be the first to know when we launch.
            </p>

            {/* Countdown Timer */}
            <div className="grid grid-cols-4 gap-4 max-w-lg mx-auto mb-12">
              {[
                { value: timeLeft.days, label: 'Days' },
                { value: timeLeft.hours, label: 'Hours' },
                { value: timeLeft.minutes, label: 'Minutes' },
                { value: timeLeft.seconds, label: 'Seconds' }
              ].map((item, index) => (
                <div 
                  key={index}
                  className="bg-slate-900/80 backdrop-blur-sm rounded-2xl p-4 border border-slate-800"
                >
                  <div className="text-3xl md:text-5xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {String(item.value).padStart(2, '0')}
                  </div>
                  <div className="text-xs md:text-sm text-slate-500 uppercase tracking-wider mt-1">
                    {item.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Email Signup */}
            <Card className="bg-slate-900/80 backdrop-blur-sm border-slate-800 max-w-md mx-auto">
              <CardContent className="p-6">
                {!isSubscribed ? (
                  <>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      Get Early Access
                    </h3>
                    <p className="text-slate-400 text-sm mb-4">
                      Join {subscriberCount > 0 ? `${subscriberCount}+ early adopters` : 'our waitlist'} and get 20% off at launch
                    </p>
                    <form onSubmit={handleSubmit} className="flex gap-2">
                      <Input
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                        data-testid="email-input"
                      />
                      <Button 
                        type="submit" 
                        className="bg-indigo-600 hover:bg-indigo-700 whitespace-nowrap"
                        disabled={isSubmitting}
                        data-testid="subscribe-btn"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center gap-2">
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Joining...
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            Notify Me
                          </span>
                        )}
                      </Button>
                    </form>
                    <p className="text-xs text-slate-500 mt-3">
                      No spam, ever. Unsubscribe anytime.
                    </p>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      You're on the list!
                    </h3>
                    <p className="text-slate-400 text-sm">
                      We'll email you the moment we launch. Get ready for 20% off!
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </section>

        {/* What's Coming */}
        <section className="py-16 px-4 bg-slate-900/50">
          <div className="max-w-6xl mx-auto">
            <h2 
              className="text-2xl md:text-3xl font-bold text-center mb-12"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              What's Launching
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {products.map((product, index) => (
                <Card 
                  key={index}
                  className="bg-slate-900/80 border-slate-800 hover:border-slate-700 transition-all"
                >
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 ${product.color} rounded-xl flex items-center justify-center text-white mb-4`}>
                      {product.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {product.name}
                    </h3>
                    <p className="text-slate-400">
                      {product.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Early Bird Perks */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 
              className="text-2xl md:text-3xl font-bold text-center mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Early Bird Perks
            </h2>
            <p className="text-slate-400 text-center mb-12">
              Sign up now and get exclusive benefits
            </p>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
              {earlyBirdPerks.map((perk, index) => (
                <div 
                  key={index}
                  className="bg-slate-900/50 rounded-xl p-4 text-center border border-slate-800"
                >
                  <div className="w-10 h-10 bg-indigo-500/20 rounded-full flex items-center justify-center mx-auto mb-3 text-indigo-400">
                    {perk.icon}
                  </div>
                  <p className="text-sm text-slate-300">{perk.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Founder Note */}
        <section className="py-16 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="mb-6">
              <UpliftShield size={50} color="#6366F1" />
            </div>
            <p className="text-slate-400 italic mb-4">
              "I'm building Uplift to help people rise in every area that matters. 
              Career success, business growth, financial freedom. Join me on this journey."
            </p>
            <p className="text-white font-medium">
              — Robert Elder III, Founder
            </p>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 px-4 border-t border-slate-800">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-slate-500 text-sm">
              © 2024-2025 Uplift III / Robert Elder III. All Rights Reserved.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default LaunchCountdown;
